// campus-app/frontend/src/pages/RegistroDemo.tsx
/**
 * RegistroDemo · Página pública de registro para el plan demo-start.
 * URL: /registro?plan=demo-start
 */
import { useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';

const VALID_PLANS = new Set(['demo-start']);
const API_URL = import.meta.env.VITE_API_URL ?? '';

export default function RegistroDemo(): JSX.Element {
  const [searchParams] = useSearchParams();
  const plan = searchParams.get('plan') ?? '';
  const isValidPlan = VALID_PLANS.has(plan);

  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleEmailSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!isValidPlan) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch(`${API_URL}/api/public/demo/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase(), plan }),
      });
      if (resp.status === 429) {
        setError('Demasiadas solicitudes · espera un momento e inténtalo de nuevo');
        return;
      }
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        setError((data as { detail?: string })?.detail ?? 'Ha ocurrido un error · inténtalo de nuevo');
        return;
      }
      setSuccess(true);
    } catch {
      setError('Error de conexión · comprueba tu internet e inténtalo de nuevo');
    } finally {
      setLoading(false);
    }
  }

  if (success) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-canvas px-4">
        <div className="w-full max-w-md text-center">
          <div className="card p-10">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-green-500/15 text-4xl">
              ✅
            </div>
            <h1 className="text-xl font-bold text-ink">¡Revisa tu email!</h1>
            <p className="mt-3 text-sm text-ink/70">
              Enviamos el enlace de acceso a{' '}
              <strong className="text-ink">{email}</strong>.{' '}
              Válido 1 hora. Si no lo ves, revisa spam.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!isValidPlan) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-canvas px-4">
        <div className="card w-full max-w-sm p-8 text-center">
          <p className="text-sm text-ink/70">
            Enlace no válido. Usa el link que te enviamos por email.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-canvas">
      {/* Navbar mínima */}
      <header className="flex items-center px-6 py-5 md:px-12">
        <img src="/robotea-logo-dark.png" alt="Robotea" className="h-7 w-auto" />
      </header>

      {/* Layout 2 columnas */}
      <main className="flex flex-1 items-center">
        <div className="mx-auto grid w-full max-w-5xl grid-cols-1 gap-10 px-6 py-8 md:px-12 lg:grid-cols-2 lg:gap-16">

          {/* Izquierda — propuesta de valor */}
          <div className="flex flex-col justify-center">
            <span className="mb-4 inline-flex w-fit items-center gap-1.5 rounded-full border border-violet-500/30 bg-violet-500/10 px-3 py-1 text-xs font-semibold text-violet-300">
              🎁 Gratis · Sin tarjeta · 30 días
            </span>

            <h1 className="text-3xl font-bold leading-tight text-ink md:text-4xl">
              Prueba Robotea Start
              <br />
              <span className="text-violet-400">sin arriesgar nada</span>
            </h1>

            <p className="mt-4 text-base text-ink/65 leading-relaxed">
              Instala <strong className="text-ink">Golden Bot Demo</strong> en tu MT4/MT5 y observa cómo opera un robot real — en simulación, sin capital real.
            </p>

            {/* 3 bullets compactos */}
            <ul className="mt-7 space-y-3">
              {[
                ['🤖', 'Golden Bot Demo preconfigurado para MT4 y MT5'],
                ['📚', 'Campus completo: formación, señales y soporte'],
                ['🛡️', 'Sin riesgo — modo simulación, cero capital real'],
              ].map(([icon, text]) => (
                <li key={text} className="flex items-center gap-3 text-sm text-ink/70">
                  <span className="text-base">{icon}</span>
                  {text}
                </li>
              ))}
            </ul>

            {/* Separador sutil */}
            <div className="mt-8 flex items-center gap-3">
              <div className="h-px flex-1 bg-ink/10" />
              <span className="text-xs text-ink/30">acceso en segundos</span>
              <div className="h-px flex-1 bg-ink/10" />
            </div>

            <p className="mt-4 text-xs text-ink/40">
              Más de 200 alumnos ya operan con robots Robotea
            </p>
          </div>

          {/* Derecha — formulario */}
          <div className="flex flex-col justify-center">
            <div className="card p-8">
              <h2 className="mb-1 text-lg font-bold text-ink">Activa tu acceso demo</h2>
              <p className="mb-6 text-sm text-ink/55">Sin contraseña · recibes el enlace al instante</p>

              {error && (
                <div className="mb-4 rounded-lg border border-red-400/30 bg-red-500/10 p-3 text-sm text-red-400">
                  {error}
                </div>
              )}

              <form onSubmit={handleEmailSubmit} className="space-y-4">
                <input
                  id="demo-email"
                  type="email"
                  required
                  autoComplete="email"
                  placeholder="tu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input w-full"
                  disabled={loading}
                />
                <button
                  type="submit"
                  disabled={loading || !email.trim()}
                  className="btn-primary w-full py-3 text-sm font-semibold disabled:opacity-50"
                  data-testid="demo-register-submit"
                >
                  {loading ? 'Enviando...' : 'Recibir acceso gratuito →'}
                </button>
              </form>

              {/* Checklist compacto */}
              <ul className="mt-5 space-y-1.5">
                {[
                  'Golden Bot Demo instalable en MT4/MT5',
                  'Guía de instalación paso a paso',
                  'Soporte de activación por WhatsApp',
                  '30 días sin compromiso',
                ].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-xs text-ink/55">
                    <span className="text-green-400">✓</span>
                    {item}
                  </li>
                ))}
              </ul>

              <p className="mt-5 text-center text-xs text-ink/35">
                Al registrarte aceptas los{' '}
                <a href="/terminos" className="underline hover:text-ink/60">Términos</a>
                {' '}y la{' '}
                <a href="/privacidad" className="underline hover:text-ink/60">Privacidad</a>.
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
